// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Sixty inserts, one draw call (M3-15, ARCH-011).
//
// A machine's inserts are the parts a player actually reads. NEON NOVA has 38 of them, driven
// by 53 lamp actions in its rules, and every one of those actions has been firing correctly
// into a renderer that never looked.
//
// **They are not lights.** Thirty-eight dynamic lights is more than most phones will accept at
// all and more than any of them will accept at 90 Hz in a headset, and it would be the wrong
// picture anyway: an insert is a translucent panel let into the wood with a bulb underneath,
// so it *emits* — it does not illuminate the playfield around it. ARCH-011 says emissive
// texels driven by one lamp-state texture, and that is exactly this:
//
//   * An **insert map**, baked once at load. One texel per playfield texel, holding *which*
//     lamp owns it and how much of it that lamp covers.
//   * A **state texture**, one texel per lamp, holding that lamp's colour times its current
//     brightness. 43 texels, rewritten each frame; the upload is smaller than this comment.
//   * A **shader hook** on the playfield's own material, adding the one to the other.
//
// Cost: one extra texture fetch per playfield fragment, no extra geometry, no extra draw
// calls, and no lights. Adding a 200-insert machine changes none of those numbers.
//
// The index is stored as a *ratio*. A canvas anti-aliases every fill, so an edge texel of an
// insert holds a blend of the shape's colour and the background — which would corrupt an index
// stored raw, turning the rim of every insert into a different lamp. Writing the index in red
// and full coverage in green means both fade together, so `red / green` recovers the index
// exactly at any coverage, and green *is* the coverage. That one trick is why this works with
// an ordinary 2-D canvas instead of a manual rasteriser.

import { ClampToEdgeWrapping, DataTexture, FloatType, LinearFilter, LinearMipmapLinearFilter, NearestFilter, RGBAFormat, Texture, UnsignedByteType } from 'three'
import type { MachineDefinition, MachineLamp } from '@core/machine'
import { lampOutline, toTextureUv } from './lampShapes'
import { chasePhases, lampBrightness } from './lampStates'
import type { Canvas2D } from '../fx/artProgram'
import bumperFinish from '../../SourceArt/PierHardware/finish-06.json'
import { createLightingSample, type ComponentLightingState } from './componentLightingState'
import { buildInsertOptics } from './insertOptics'

/** What a lamp's brightness is read from. The rule engine satisfies this directly. */
export type LampStateReader = (id: string) => number

/**
 * One lamp as the map records it: which index it was given, and why it got none.
 *
 * Returned by `paintLampMap` so the whole baking step can be checked without a GPU or a real
 * canvas — the same trick the art engine uses, and for the same reason. The failure this
 * catches is an insert painted at the wrong end of the playfield, which does not look like a
 * texture bug: the lane lamps appear beside the flippers and it reads as a layout mistake.
 */
export interface LampMapEntry {
  readonly id: string
  /** 1-based, because 0 means "no insert here" and that is most of the playfield. */
  readonly index: number
}

export interface PaintedLampMap {
  readonly painted: readonly LampMapEntry[]
  readonly skipped: readonly string[]
}

/**
 * Paint every drawable insert into a 2-D context, and say what went where.
 *
 * Takes a structural `Canvas2D` rather than a `CanvasRenderingContext2D`, so a test can hand
 * it a recorder and assert the coordinates. Everything about the map that can be wrong —
 * which lamps are drawn, what index each gets, and where on the surface each one lands — is
 * decided here, in a function that needs no browser.
 */
export function paintLampMap(
  ctx: Canvas2D,
  definition: MachineDefinition,
  mapWidth: number,
  mapHeight: number,
): PaintedLampMap {
  const { width, length } = definition.dimensions.playfield
  const painted: LampMapEntry[] = []
  const skipped: string[] = []

  for (const lamp of definition.lamps ?? []) {
    const outline = lampOutline(lamp)
    if (outline === undefined) {
      skipped.push(
        lamp.region !== undefined
          ? `${lamp.id}: a ${lamp.kind} lights a region rather than an insert (M3-16)`
          : `${lamp.id}: no position, so there is nowhere to put an insert`,
      )
      continue
    }
    if (lamp.kind === 'flasher') {
      skipped.push(`${lamp.id}: a flasher is a bulb above the playfield, not an insert (M3-16)`)
      continue
    }
    const index = painted.length + 1
    painted.push({ id: lamp.id, index })
    fillOutline(ctx, outline, width, length, mapWidth, mapHeight, index)
  }

  return { painted, skipped }
}

export interface BuiltLamps {
  /** Shared local lens normals/distance and per-lamp transforms. Device-owned pools are excluded. */
  readonly optics?: {readonly atlas:DataTexture;readonly parameters:DataTexture;readonly selector:DataTexture;readonly selectorGroups:number;readonly tileCount:number;readonly tileSize:number}
  /** Which lamp owns each playfield texel, and how much of it. */
  readonly map: Texture
  /** One texel per lamp: colour × brightness. */
  readonly state: DataTexture
  readonly lampCount: number
  /** Lamps with no insert to draw, each with the reason. */
  readonly skipped: readonly string[]
  /** Recompute the state texture for a step. Call once a frame. */
  /** `reduced` is NFR-026's flash reduction: same states, no hard edges. */
  readonly update: (read: LampStateReader, step: number, reduced?: boolean, lighting?: ComponentLightingState) => void
  readonly dispose: () => void
}

/**
 * Bake the insert map and prepare the state texture.
 *
 * `resolution` is the insert map's own, and is deliberately independent of the playfield art's
 * — the map holds shapes, not detail, and a 9 mm insert needs far fewer texels than the
 * artwork around it. Half the art's resolution is four times less memory for a picture nobody
 * can tell apart.
 */
export function buildLamps(
  definition: MachineDefinition,
  resolution: readonly [number, number] = [512, 1024],
  _illustrated = false,
): BuiltLamps {
  const lamps = definition.lamps ?? []
  const [mapWidth, mapHeight] = resolution

  const canvas = document.createElement('canvas')
  canvas.width = mapWidth
  canvas.height = mapHeight
  const ctx = canvas.getContext('2d')
  if (ctx === null) throw new Error('buildLamps: the browser refused a 2-D context')

  const { painted, skipped } = paintLampMap(
    ctx as unknown as Canvas2D,
    definition,
    mapWidth,
    mapHeight,
  )
  const byId = new Map(lamps.map((l) => [l.id, l]))
  const drawn: MachineLamp[] = painted.map((p) => byId.get(p.id)!)

  const map = new Texture(canvas)
  // Linear on the map, and it is safe: the *ratio* survives filtering, because both channels
  // are interpolated by the same weights. Nearest would give the inserts hard, stair-stepped
  // rims at the glancing angles a playfield is always seen from.
  map.magFilter = LinearFilter
  map.minFilter = LinearFilter
  map.generateMipmaps = false
  map.wrapS = ClampToEdgeWrapping
  map.wrapT = ClampToEdgeWrapping
  // `true`, matching the playfield art's own upload, and the two **must** agree: they are
  // sampled by the same `vMapUv`, so a map uploaded the other way up puts every insert at the
  // opposite end of the playfield. The lane inserts appear beside the flippers and the bumper
  // rings appear in the drain, which does not read as an upside-down texture — it reads as
  // somebody having laid the lamps out wrongly.
  map.flipY = true
  map.needsUpdate = true

  const count = Math.max(1, drawn.length)
  const optical = buildInsertOptics(definition)
  let optics:BuiltLamps['optics']
  if(optical!==undefined&&optical.physical.length>0){
    const atlas=new DataTexture(optical.atlas,optical.atlasWidth,optical.atlasHeight,RGBAFormat,UnsignedByteType)
    const parameters=new DataTexture(optical.parameters,optical.count,4,RGBAFormat,FloatType)
    parameters.magFilter=NearestFilter;parameters.minFilter=NearestFilter;parameters.generateMipmaps=false;parameters.needsUpdate=true
    const selector=new DataTexture(optical.selector.pixels,optical.selector.width,optical.selector.height,RGBAFormat,UnsignedByteType)
    selector.magFilter=NearestFilter;selector.minFilter=NearestFilter;selector.generateMipmaps=false;selector.flipY=false;selector.needsUpdate=true
    optics={atlas,parameters,selector,selectorGroups:optical.selector.groups,tileCount:optical.tileCount,tileSize:optical.atlasHeight}
  }
  if (optics !== undefined) {
    optics.atlas.magFilter = LinearFilter
    optics.atlas.minFilter = LinearMipmapLinearFilter
    optics.atlas.generateMipmaps = true
    optics.atlas.flipY = false // every padded Blender tile is bottom row first
    optics.atlas.needsUpdate = true
  }
  const state = new DataTexture(new Uint8Array(count * 4), count, 1, RGBAFormat, UnsignedByteType)
  state.magFilter = NearestFilter
  state.minFilter = NearestFilter
  state.generateMipmaps = false
  state.needsUpdate = true

  const colours = drawn.map((l) => parseColour(l.colour))
  const bumperLamps=new Set(definition.devices.filter(d=>d.kind==='popBumper').map(d=>d.lamp))
  const levels=drawn.map(l=>bumperLamps.has(l.id)?bumperFinish.ring.boardIntensity:1)
  const phases = chasePhases(groupsOf(drawn))
  const data = state.image.data as Uint8Array
  const response = createLightingSample()

  const update = (read: LampStateReader, step: number, reduced = false, lighting?: ComponentLightingState): void => {
    for (let i = 0; i < drawn.length; i++) {
      const lamp = drawn[i]!
      let level = lampBrightness(read(lamp.id), step, phases.get(lamp.id) ?? 0, reduced)
      const owner = lighting?.lampOwners.get(lamp.id)
      if (lighting !== undefined && owner !== undefined) {
        lighting.sample(owner, level, reduced, response)
        level = Math.max(level, response.activity * 0.65)
      }
      level *= levels[i]!
      const [r, g, b] = colours[i]!
      data[i * 4] = r * level
      data[i * 4 + 1] = g * level
      data[i * 4 + 2] = b * level
      data[i * 4 + 3] = 255
    }
    state.needsUpdate = true
  }
  update(() => 0, 0)

  return {
    ...(optics === undefined ? {} : { optics }),
    map,
    state,
    lampCount: drawn.length,
    skipped,
    update,
    dispose: () => {
      map.dispose()
      state.dispose()
      optics?.atlas.dispose()
      optics?.selector.dispose()
      optics?.parameters.dispose()
    },
  }
}

/** Lamp ids by the group they declare, for the chase phases. */
function groupsOf(lamps: readonly MachineLamp[]): Map<string, string[]> {
  const out = new Map<string, string[]>()
  for (const lamp of lamps) {
    if (lamp.group === undefined) continue
    const list = out.get(lamp.group)
    if (list) list.push(lamp.id)
    else out.set(lamp.group, [lamp.id])
  }
  return out
}

/**
 * Paint one insert into the map.
 *
 * `rgb(index, 255, 0)`: red carries the index, green carries full coverage, and the canvas
 * blends both toward zero at the edge by the same fraction — so the shader divides one by the
 * other and gets the index back exactly, whatever the coverage.
 */
function fillOutline(
  ctx: Canvas2D,
  outline: readonly (readonly [number, number])[],
  width: number,
  length: number,
  mapWidth: number,
  mapHeight: number,
  index: number,
): void {
  ctx.save()
  ctx.beginPath()
  for (let i = 0; i < outline.length; i++) {
    const uv = toTextureUv(outline[i]!, width, length)
    // A canvas draws `y` downward and the playfield's `y` runs up, so the far end of the
    // playfield is canvas row 0 — which `flipY: true` then puts back at `v = 1`, where the
    // art has it too.
    const x = uv[0] * mapWidth
    const y = (1 - uv[1]) * mapHeight
    if (i === 0) ctx.moveTo(x, y)
    else ctx.lineTo(x, y)
  }
  ctx.closePath()
  ctx.fillStyle = `rgb(${index}, 255, 0)`
  ctx.fill()

  // NFR-021: "lit inserts differ in brightness **and in a shape or pattern cue**". Brightness on
  // its own is what an emissive fill already gives, and it is not enough — a player who cannot
  // separate the hues sees one grey shape get lighter, which is a difficulty setting rather than
  // a cue. The rim is the pattern: a lit insert reads as a bright *ring around a shape*, an unlit
  // one as flat wood, and the difference survives being colour-blind, being on a phone in
  // sunlight, and being at the far end of an arcade.
  //
  // It is also what a real insert looks like. The lens sits proud of the wood and its edge
  // catches the bulb, which is why photographs of a lit playfield are full of outlines.
  //
  // Written into the blue channel, which the map was not using. Red carries the lamp index and
  // green the coverage; the shader recovers both by dividing by green, and recovers the rim the
  // same way, so bilinear filtering blurs the rim rather than corrupting the index.
  ctx.strokeStyle = `rgb(${index}, 255, 255)`
  ctx.lineWidth = RIM_TEXELS
  ctx.stroke()
  ctx.restore()
}

/**
 * How wide the rim is, in insert-map texels.
 *
 * Three, against a map whose smallest insert is a couple of dozen texels across. Wider stops
 * reading as an edge and starts reading as a thicker insert; narrower disappears into the
 * filtering on the tiers that render the map at half resolution.
 */
export const RIM_TEXELS = 3

/** `#rrggbb` to bytes. A malformed colour lights white rather than black. */
export function parseColour(colour: string): readonly [number, number, number] {
  const hex = colour.trim().replace('#', '')
  if (hex.length !== 6) return [255, 255, 255]
  const n = Number.parseInt(hex, 16)
  if (!Number.isFinite(n)) return [255, 255, 255]
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff]
}

/**
 * The shader hook, added to the playfield's own material.
 *
 * Injected after `emissivemap_fragment` so it adds to `totalEmissiveRadiance` — the same
 * channel an emissive map feeds — which means the inserts go through tone mapping and the
 * bloom that follows it exactly as any other light in the scene would, instead of being
 * pasted on afterwards at whatever brightness happened to look right on one monitor.
 */
/**
 * How much brighter a lit insert's rim is than its middle.
 *
 * Enough to be a *shape* at a glance and not so much that the insert becomes an outline with a
 * dark centre. Interpolated into the shader source rather than passed as a uniform: it is a
 * constant of the look, not something anything changes at runtime, and a uniform nobody sets is
 * a knob that eventually gets set by accident.
 */
export const LAMP_RIM_GAIN = '0.85'

export const LAMP_FRAGMENT_HOOK = `
  vec4 lampCell = texture2D( uLampMap, vMapUv );
  if ( lampCell.g > 0.02 ) {
    float lampIndex = floor( lampCell.r / lampCell.g * 255.0 + 0.5 );
    float packedSurface=uOpticsTileSize>1.0?texture2D(uOpticsParams,vec2((lampIndex-.5)/uLampCount,.375)).w:0.0;
    float lampHeight=packedSurface>=1.0?packedSurface-1.0:-packedSurface;
    if ( lampIndex >= 1.0 && abs(vInsertSurfaceZ-lampHeight)<.002 ) {
      vec2 stateUv = vec2( ( lampIndex - 0.5 ) / uLampCount, 0.5 );
      vec3 lampColour = texture2D( uLampState, stateUv ).rgb;
      // NFR-021's pattern cue. Blue over green is the rim mask, recovered the same way the index
      // is — so the edge of a lit insert is brighter than its middle and the state is legible
      // without reading the colour at all.
      float lampRim = clamp( lampCell.b / lampCell.g, 0.0, 1.0 );
      totalEmissiveRadiance += lampColour * lampCell.g * uLampGain * ( 1.0 + lampRim * ${LAMP_RIM_GAIN} );
    }
  }
`

export const LAMP_UNIFORM_DECLARATIONS = `
  uniform sampler2D uLampMap;
  uniform sampler2D uLampState;
  uniform float uLampCount;
  uniform float uLampGain;
`
